import React, { useEffect, useState, ChangeEvent, FormEvent } from 'react';
import styled from 'styled-components';
import { EthAddress, GasPrices, AutoGasSetting } from '@darkforest_eth/types';
import { useAccount, useUIManager } from '../Utils/AppHooks';
import { ModalHook, ModalName, ModalPane } from '../Views/ModalPane';
import { Text, White, Green, Red } from '../Components/Text';
import { Padded, Section, SectionHeader, Spacer } from '../Components/CoreUI';
import { Btn } from '../Components/Btn';
import { Input } from '../Components/Input';
import { useEmitterValue } from '../Utils/EmitterHooks';
import { BooleanSetting, MultiSelectSetting, Setting } from '../Utils/SettingsHooks';
import { getLocalGasPrices } from '../../Backend/Network/LocalGasPriceAPI';
import { Chunk } from '../../_types/global/GlobalTypes';
import TutorialManager from '../../Backend/GameLogic/TutorialManager';
import Viewport, { getDefaultScroll } from '../Game/Viewport';
import { EthConnection } from '@darkforest_eth/network';
import { exportStaticCacheFile } from '../../Backend/Utils/StaticRecorder';

const SCROLL_MIN = 0.0001 * 10000;
const SCROLL_MAX = 0.01 * 10000;
const DEFAULT_SCROLL = Math.round(10000 * (getDefaultScroll() - 1));

const Range = styled.input``;

const SettingsContent = styled(Padded)`
  width: 500px;
  height: 500px;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  text-align: justify;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  & > span:first-child {
    flex-grow: 1;
  }
`;

const ScrollSpeedInput = styled(Input)`
  padding: 2px 2px;
  width: 8em;
  height: min-content;
`;

export function SettingsPane({
  ethConnection,
  hook,
  privateHook,
}: {
  ethConnection: EthConnection;
  hook: ModalHook;
  privateHook: ModalHook;
}) {
  const uiManager = useUIManager();
  const account = useAccount(uiManager);
  
  // Use local gas price API to avoid CORS issues
  const [gasPrices, setGasPrices] = useState<GasPrices | null>(null);

  useEffect(() => {
    const fetchGasPrices = async () => {
      const prices = await getLocalGasPrices();
      setGasPrices(prices);
    };

    fetchGasPrices();
    // Update every 60 seconds
    const interval = setInterval(fetchGasPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  const [rpcUrl, setRpcURL] = useState<string>(ethConnection.getRpcEndpoint());
  const onChangeRpc = () => {
    ethConnection
      .setRpcUrl(rpcUrl)
      .then(() => {
        localStorage.setItem('XDAI_RPC_ENDPOINT_v5', rpcUrl);
      })
      .catch(() => {
        setRpcURL(ethConnection.getRpcEndpoint());
      });
  };

  const [balance, setBalance] = useState<number>(0);

  useEffect(() => {
    if (!uiManager) return;
    const updateBalance = () => {
      setBalance(uiManager.getMyBalance());
    };

    updateBalance();
    const intervalId = setInterval(updateBalance, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [uiManager]);

  const [failure, setFailure] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [importMapByTextBoxValue, setImportMapByTextBoxValue] = useState('');
  useEffect(() => {
    if (failure) {
      setSuccess('');
    }
  }, [failure]);
  useEffect(() => {
    if (success) {
      setFailure('');
    }
  }, [success]);
  const onExportMap = async () => {
    if (uiManager) {
      const chunks = uiManager.getExploredChunks();
      const chunksAsArray = Array.from(chunks);
      try {
        const map = JSON.stringify(chunksAsArray);
        await window.navigator.clipboard.writeText(map);
        setSuccess('Copied map!');
      } catch (err) {
        console.error(err);
        setFailure('Failed to export');
      }
    } else {
      setFailure('Unable to export map right now.');
    }
  };
  const onImportMapFromTextBox = async () => {
    try {
      const chunks = JSON.parse(importMapByTextBoxValue);
      await uiManager.bulkAddNewChunks(chunks as Chunk[]);
      setImportMapByTextBoxValue('');
    } catch (e) {
      setFailure('Invalid map data. Check the data in your clipboard.');
    }
  };
  const onImportMap = async () => {
    if (uiManager) {
      let input;
      try {
        input = await window.navigator.clipboard.readText();
      } catch (err) {
        console.error(err);
        setFailure('Unable to import map. Did you allow clipboard access?');
        return;
      }

      let chunks;
      try {
        chunks = JSON.parse(input);
      } catch (err) {
        console.error(err);
        setFailure('Invalid map data. Check the data in your clipboard.');
        return;
      }
      await uiManager.bulkAddNewChunks(chunks as Chunk[]);
      setSuccess('Successfully imported a map!');
    } else {
      setFailure('Unable to import map right now.');
    }
  };

  const [clicks, setClicks] = useState<number>(8);
  const [, setPrivateVisible] = privateHook;
  const doPrivateClick = (_e: React.MouseEvent) => {
    setClicks((x) => x - 1);
    if (clicks === 1) {
      setPrivateVisible(true);
      setClicks(5);
    }
  };

  const clipScroll = (v: number) => Math.max(Math.min(Math.round(v), SCROLL_MAX), SCROLL_MIN);
  const [scrollSpeed, setScrollSpeed] = useState<number>(DEFAULT_SCROLL);
  const onScrollChange = (e: FormEvent) => {
    const value = parseFloat((e.target as HTMLInputElement).value);
    if (!isNaN(value)) setScrollSpeed(value);
  };

  useEffect(() => {
    const scroll = localStorage.getItem('scrollSpeed');
    if (scroll) {
      setScrollSpeed(10000 * (parseFloat(scroll) - 1));
    }
  }, [setScrollSpeed]);

  useEffect(() => {
    if (!Viewport.instance) return;
    Viewport.instance.setMouseSensitivty(scrollSpeed / 10000);
  }, [scrollSpeed]);

  return (
    <ModalPane hook={hook} title={'Settings'} name={ModalName.Hats}>
      <SettingsContent>
        <Section>
          <SectionHeader>Burner Wallet Info</SectionHeader>
          <Row>
            <span>Public Key</span>
            <span>{account}</span>
          </Row>
          <Row>
            <span>Balance</span>
            <span>{balance}</span>
          </Row>
        </Section>

        <Section>
          <SectionHeader>Developer: Static RPC Cache</SectionHeader>
          <Spacer height={8} />
          <Row>
            <span>Mode (DF_OFFLINE_CACHE_MODE)</span>
            <Input
              wide
              value={localStorage.getItem('DF_OFFLINE_CACHE_MODE') || ''}
              placeholder={'disabled | record | strict-static'}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                localStorage.setItem('DF_OFFLINE_CACHE_MODE', e.target.value)
              }
            />
          </Row>
          <Spacer height={8} />
          <Row>
            <span>Static URL (DF_STATIC_CACHE_URL)</span>
            <Input
              wide
              value={localStorage.getItem('DF_STATIC_CACHE_URL') || '/public/offline/df-read-cache.json'}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                localStorage.setItem('DF_STATIC_CACHE_URL', e.target.value)
              }
            />
          </Row>
          <Spacer height={8} />
          <Row>
            <span>No Persist Writes (DF_NO_PERSIST_MUTATION)</span>
            <Input
              wide
              value={localStorage.getItem('DF_NO_PERSIST_MUTATION') || 'false'}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                localStorage.setItem('DF_NO_PERSIST_MUTATION', e.target.value)
              }
            />
          </Row>
          <Spacer height={8} />
          <Btn wide onClick={() => exportStaticCacheFile()}>Export Static Cache JSON</Btn>
        </Section>

        <Section>
          <SectionHeader>Gas Price</SectionHeader>
          Your gas price setting determines the price you pay for each transaction. A higher gas
          price means your transactions will be prioritized by the blockchain, making them confirm
          faster. We recommend using the auto average setting. All auto settings prices are pulled
          from an oracle and are capped at 15 gwei.
          <Spacer height={16} />
          <MultiSelectSetting
            wide
            uiManager={uiManager}
            setting={Setting.GasFeeGwei}
            values={[
              '1',
              '2',
              '5',
              '10',
              '20',
              '40',
              AutoGasSetting.Slow,
              AutoGasSetting.Average,
              AutoGasSetting.Fast,
            ]}
            labels={[
              '1 gwei (default)',
              '2 gwei (faster)',
              '5 gwei (turbo)',
              '10 gwei (mega turbo)',
              '20 gwei (need4speed)',
              '40 gwei (gigafast)',
              gasPrices ? `slow auto (~${gasPrices.slow} gwei)` : 'slow auto (unavailable)',
              gasPrices ? `average auto (~${gasPrices.average} gwei)` : 'average auto (unavailable)',
              gasPrices ? `fast auto (~${gasPrices.fast} gwei)` : 'fast auto (unavailable)',
            ]}
          />
        </Section>

        <Section>
          <SectionHeader>Burner Wallet Info (Private)</SectionHeader>
          Your secret key, together with your home planet's coordinates, grant you access to your
          Dark Forest account on different browsers. You should save this info somewhere on your
          computer.
          <Spacer height={16} />
          <Red>WARNING:</Red> Never ever send this to anyone!
          <Spacer height={8} />
          <Btn wide onClick={doPrivateClick}>
            Click {clicks} times to view info
          </Btn>
        </Section>

        <Section>
          <SectionHeader>Auto Confirm Transactions</SectionHeader>
          Whether or not to auto-confirm all transactions, except purchases. This will allow you to
          make moves, spend silver on upgrades, etc. without requiring you to confirm each
          transaction. However, the client WILL ask for confirmation before sending transactions
          that spend wallet funds.
          <Spacer height={16} />
          <BooleanSetting
            uiManager={uiManager}
            setting={Setting.AutoApproveNonPurchaseTransactions}
            settingDescription={'auto confirm non-purchase transactions'}
          />
        </Section>

        <Section>
          <SectionHeader>Import and Export Map Data</SectionHeader>
          <Red>WARNING:</Red> Maps from others could be altered and are not guaranteed to be
          correct!
          <Spacer height={16} />
          <Input
            wide
            value={importMapByTextBoxValue}
            placeholder={'Paste map contents here'}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setImportMapByTextBoxValue(e.target.value)
            }
          />
          <Spacer height={8} />
          <Btn
            wide
            onClick={onImportMapFromTextBox}
            disabled={importMapByTextBoxValue.length === 0}
          >
            Import Map From Above
          </Btn>
          <Spacer height={8} />
          <Btn wide onClick={onExportMap}>
            Copy Map to Clipboard
          </Btn>
          <Spacer height={8} />
          <Btn wide onClick={onImportMap}>
            Import Map from Clipboard
          </Btn>
          <Spacer height={8} />
          <Green>{success}</Green>
          <Red>{failure}</Red>
        </Section>

        <Section>
          <SectionHeader>Change RPC Endpoint</SectionHeader>
          <Spacer height={8} />
          Current RPC Endpoint: {rpcUrl}
          <Spacer height={8} />
          <Input wide value={rpcUrl} onChange={(e) => setRpcURL(e.target.value)} />
          <Spacer height={8} />
          <Btn wide onClick={onChangeRpc}>
            Change RPC URL
          </Btn>
        </Section>

        <Section>
          <SectionHeader>Metrics Opt Out</SectionHeader>
          We collect a minimal set of data and statistics such as SNARK proving times, average
          transaction times across browsers, and xDAI transaction errors, to help us optimize
          performance and fix bugs. This does not include personal data like email or IP address.
          <Spacer height={8} />
          <BooleanSetting
            uiManager={uiManager}
            setting={Setting.OptOutMetrics}
            settingDescription='metrics opt out'
          />
        </Section>

        <Section>
          <SectionHeader>Performance</SectionHeader>
          Some performance settings. These will definitely be changed as we zero in on the
          performance bottlenecks in this game.
          <Spacer height={16} />
          <BooleanSetting
            uiManager={uiManager}
            setting={Setting.HighPerformanceRendering}
            settingDescription='performance mode'
          />
        </Section>

        <Section>
          <SectionHeader>Show notifications for MOVE</SectionHeader>
          <Spacer height={8} />
          <BooleanSetting
            uiManager={uiManager}
            setting={Setting.MoveNotifications}
            settingDescription='move notifications'
          />
        </Section>

        <Section>
          <SectionHeader>Scroll speed</SectionHeader>
          <Spacer height={8} />
          <ScrollContainer>
            <Range
              type='range'
              value={clipScroll(scrollSpeed)}
              min={SCROLL_MIN}
              max={SCROLL_MAX}
              step={SCROLL_MIN / 10}
              onChange={onScrollChange}
            />
            <Spacer width={16} />
            <ScrollSpeedInput value={scrollSpeed} onChange={onScrollChange} />
          </ScrollContainer>
        </Section>

        <Section>
          <SectionHeader>Reset Tutorial</SectionHeader>
          <Spacer height={8} />
          <Btn wide onClick={() => TutorialManager.getInstance().reset(uiManager.getAccount())}>
            Reset Tutorial
          </Btn>
        </Section>

        <Section>
          <SectionHeader>Disable Default Shortcuts</SectionHeader>
          If you'd like to use custom shortcuts via a plugin, you can disable the default shortcuts
          here.
          <Spacer height={8} />
          <BooleanSetting
            uiManager={uiManager}
            setting={Setting.DisableDefaultShortcuts}
            settingDescription='toggle disable default shortcuts'
          />
        </Section>

        <Section>
          <SectionHeader>Enable Experimental Features</SectionHeader>
          Features that aren't quite ready for production but we think are cool.
          <Spacer height={8} />
          <BooleanSetting
            uiManager={uiManager}
            setting={Setting.ExperimentalFeatures}
            settingDescription='toggle expeirmental features'
          />
        </Section>
      </SettingsContent>
    </ModalPane>
  );
}

const ScrollContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
`;
